/**
 * Panel de Finanzas Completa del Evento.
 * Consume /api/erp/eventos/{id}/finanzas-completo (todo pivota en evento_id).
 * NO recaptura información: derivada de pagos + inventario + personal + compras.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, DollarSign, Package, UserCog, ShoppingCart, RefreshCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

export default function FinanzasEventoPanel({ eventoId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!eventoId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/erp/eventos/${eventoId}/finanzas-completo`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <p className="text-sm text-stone-500">Cargando finanzas…</p>;
  if (!data) return null;

  const isPositivo = data.utilidad >= 0;

  return (
    <Card className="border-stone-200" data-testid="finanzas-evento-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Finanzas del evento
        </CardTitle>
        <Button variant="outline" size="sm" onClick={cargar} data-testid="btn-refresh-finanzas">
          <RefreshCcw className="w-3 h-3" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Utilidad grande */}
        <div className={`p-4 rounded-lg ${isPositivo ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
          <div className="flex items-center gap-2 mb-1">
            {isPositivo ? <TrendingUp className="w-4 h-4 text-emerald-700" /> : <TrendingDown className="w-4 h-4 text-rose-700" />}
            <span className={`text-xs uppercase tracking-wider ${isPositivo ? "text-emerald-700" : "text-rose-700"}`}>Utilidad estimada</span>
          </div>
          <p className={`font-display text-3xl ${isPositivo ? "text-emerald-900" : "text-rose-900"}`} data-testid="utilidad-valor">
            {fmt(data.utilidad)} <span className="text-sm">MXN</span>
          </p>
          <p className={`text-xs mt-1 ${isPositivo ? "text-emerald-700" : "text-rose-700"}`}>
            Margen: <strong data-testid="margen-valor">{data.margen_pct}%</strong>
          </p>
        </div>

        {/* Desglose */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-3 bg-stone-50 rounded border border-stone-200">
            <p className="text-xs uppercase tracking-wider text-stone-500">Ingresos</p>
            <p className="font-display text-xl text-stone-900" data-testid="ingresos-valor">{fmt(data.ingresos)}</p>
            <p className="text-[11px] text-stone-500 mt-0.5">{data.pagos_count} pagos</p>
          </div>
          <div className="p-3 bg-stone-50 rounded border border-stone-200">
            <p className="text-xs uppercase tracking-wider text-stone-500">Costos totales</p>
            <p className="font-display text-xl text-stone-900" data-testid="costos-total-valor">{fmt(data.costos.total)}</p>
          </div>
        </div>

        <div className="space-y-1 text-xs pt-2 border-t border-stone-100">
          <p className="text-stone-500 uppercase tracking-wider mb-1">Desglose de costos</p>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5"><Package className="w-3 h-3" /> Inventario ({data.consumos_count})</span>
            <span className="font-medium">{fmt(data.costos.inventario)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5"><UserCog className="w-3 h-3" /> Personal ({data.personal_count})</span>
            <span className="font-medium">{fmt(data.costos.personal)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5"><ShoppingCart className="w-3 h-3" /> Compras ({data.compras_count})</span>
            <span className="font-medium">{fmt(data.costos.compras)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
